/**
 * Breadcrumb Component
 *
 * A navigation component that shows the user's current location within
 * a hierarchical structure. Integrates with React Router for navigation.
 *
 * Validates: Requirements 2.4
 * - Requirement 2.4: THE Console SHALL provide breadcrumb navigation for nested views.
 */

import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { tokens } from '../tokens';

export interface BreadcrumbItemData {
  /** Display label for the breadcrumb item */
  label: string;
  /** Navigation href - if provided, item is clickable */
  href?: string;
  /** Optional icon to display before the label */
  icon?: React.ReactNode;
}

export interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  /** Array of breadcrumb items to display */
  items: BreadcrumbItemData[];
  /** Custom separator between items (default: chevron) */
  separator?: React.ReactNode;
  /** Maximum number of items to show before collapsing (optional) */
  maxItems?: number;
}

export interface BreadcrumbItemProps {
  /** The breadcrumb item data */
  item: BreadcrumbItemData;
  /** Whether this is the current (last) item */
  isCurrent: boolean;
}

/**
 * Default chevron separator icon
 */
function ChevronSeparator() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M6 4L10 8L6 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Ellipsis icon for collapsed items
 */
function EllipsisIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="3" cy="8" r="1.5" fill="currentColor" />
      <circle cx="8" cy="8" r="1.5" fill="currentColor" />
      <circle cx="13" cy="8" r="1.5" fill="currentColor" />
    </svg>
  );
}

/**
 * Individual breadcrumb item component.
 *
 * Features:
 * - Link items: neutral-500 text, hover: primary-600
 * - Current item: neutral-900 text, non-clickable
 * - Optional icon support
 *
 * Color contrast meets WCAG 2.1 AA standards:
 * - Link text: neutral-500 on white background (contrast ratio > 4.5:1)
 * - Current text: neutral-900 on white background (contrast ratio > 4.5:1)
 */
export const BreadcrumbItem = forwardRef<HTMLLIElement, BreadcrumbItemProps>(
  function BreadcrumbItem({ item, isCurrent }, ref) {
    const [isHovered, setIsHovered] = React.useState(false);

    const baseStyles: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: tokens.spacing[1],
      fontFamily: tokens.typography.fontFamily.sans,
      fontSize: tokens.typography.fontSize.sm,
      fontWeight: isCurrent
        ? tokens.typography.fontWeight.medium
        : tokens.typography.fontWeight.normal,
      lineHeight: tokens.typography.lineHeight.normal,
      color: isCurrent
        ? tokens.colors.neutral[900]
        : isHovered
          ? tokens.colors.primary[600]
          : tokens.colors.neutral[500],
      textDecoration: 'none',
      transition: `color ${tokens.transitions.fast}`,
      cursor: isCurrent ? 'default' : 'pointer',
    };

    const iconStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: '16px',
      height: '16px',
    };

    const content = (
      <>
        {item.icon && <span style={iconStyles}>{item.icon}</span>}
        <span>{item.label}</span>
      </>
    );

    return (
      <li ref={ref} style={{ display: 'inline-flex', alignItems: 'center' }}>
        {isCurrent || !item.href ? (
          <span
            style={baseStyles}
            aria-current={isCurrent ? 'page' : undefined}
          >
            {content}
          </span>
        ) : (
          <Link
            to={item.href}
            style={baseStyles}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {content}
          </Link>
        )}
      </li>
    );
  }
);

/**
 * Breadcrumb navigation component.
 *
 * Features:
 * - Container for breadcrumb items with proper ARIA attributes
 * - Separator between items (chevron by default)
 * - Support for collapsing middle items when maxItems is set
 * - React Router integration for navigation
 *
 * Styling:
 * - Uses design tokens for colors, spacing, typography
 * - Current item: neutral-900 text, non-clickable
 * - Link items: neutral-500 text, hover: primary-600
 * - Separator: neutral-400
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Breadcrumb
 *   items={[
 *     { label: 'Home', href: '/' },
 *     { label: 'Agents', href: '/agents' },
 *     { label: 'Agent Settings' },
 *   ]}
 * />
 *
 * // With icons
 * <Breadcrumb
 *   items={[
 *     { label: 'Home', href: '/', icon: <HomeIcon /> },
 *     { label: 'Settings' },
 *   ]}
 * />
 *
 * // With custom separator
 * <Breadcrumb
 *   items={items}
 *   separator={<span>/</span>}
 * />
 *
 * // With max items (collapses middle items)
 * <Breadcrumb
 *   items={longItemsList}
 *   maxItems={4}
 * />
 * ```
 */
export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(
  function Breadcrumb(
    { items, separator, maxItems, style, ...props },
    ref
  ) {
    const navStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      ...style,
    };

    const listStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing[2],
      listStyle: 'none',
      margin: 0,
      padding: 0,
    };

    const separatorStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      color: tokens.colors.neutral[400],
    };

    const ellipsisStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      color: tokens.colors.neutral[400],
      padding: `0 ${tokens.spacing[1]}`,
    };

    // Determine which items to display
    const getDisplayItems = (): { items: BreadcrumbItemData[]; hasCollapsed: boolean } => {
      if (!maxItems || items.length <= maxItems) {
        return { items, hasCollapsed: false };
      }

      // Always show first item, last (maxItems - 2) items, and ellipsis
      // Example: maxItems=4, items=[A,B,C,D,E,F] -> [A, ..., D, E, F]
      const firstItem = items[0];
      const lastItems = items.slice(-(maxItems - 1));
      
      return {
        items: [firstItem, ...lastItems],
        hasCollapsed: true,
      };
    };

    const { items: displayItems, hasCollapsed } = getDisplayItems();

    const separatorElement = separator || <ChevronSeparator />;

    return (
      <nav
        ref={ref}
        aria-label="Breadcrumb"
        style={navStyles}
        {...props}
      >
        <ol style={listStyles}>
          {displayItems.map((item, index) => {
            const isLast = index === displayItems.length - 1;
            const showEllipsis = hasCollapsed && index === 0;

            return (
              <React.Fragment key={`${item.label}-${index}`}>
                <BreadcrumbItem
                  item={item}
                  isCurrent={isLast}
                />
                {showEllipsis && (
                  <>
                    <li style={separatorStyles} aria-hidden="true">
                      {separatorElement}
                    </li>
                    <li style={ellipsisStyles} aria-hidden="true">
                      <EllipsisIcon />
                    </li>
                  </>
                )}
                {!isLast && (
                  <li style={separatorStyles} aria-hidden="true">
                    {separatorElement}
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ol>
      </nav>
    );
  }
);

export default Breadcrumb;
